import { AppConfig, SettingsConfig } from '@core/utils/config';
import { CompactChecklistTemplateResponse } from '@business/entities/checklist';
import { CompactDealBoardResponse } from '@business/entities/deal';
import { BoardResponse } from '@business/entities/project';
import { CompactChannelResponse } from '@business/entities/SupportChannel';
import { CommonService } from '@business/services';
import { FormSelectItem } from '@core/components/form';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { R } from '@core/utils/r';
import { CompactTMBoardResponse } from '@business/entities/tm/TimeMaterialBoardResponse';
import { ARDashboardResponse } from '@business/entities/advancedReporting';
import { VeryCompactDashboardResponse } from '@business/entities/dashboard';
import { TMEstimatorTemplateFeeResponse } from '@business/entities/tm/TMEstimatorTemplateFeeResponse';
import { TMChangeOrderTypeResponse } from '@business/entities/tm/TMChangeOrderTypeResponse';

export type GCModule =
  | 'contacts'
  | 'deals'
  | 'deal-board'
  | 'tm-board'
  | 'tm-pos'
  | 'tm-rma'
  | 'tm-pm'
  | 'tm-changeorders'
  | 'tm-summary'
  | 'tm-pricing'
  | 'tm-labor'
  | 'people'
  | 'organizations'
  | 'projects'
  | 'project-board'
  | 'template'
  | 'support'
  | 'orders'
  | 'ticketing'
  | 'categories'
  | 'marketing'
  | 'marketing-board'
  | 'marketing-checklists'
  | 'reports'
  | 'reports-billing'
  | 'reports-contacts'
  | 'reports-projects'
  | 'reports-sales'
  | 'reports-support'
  | 'reports-time-material'
  | 'companies'
  | 'users'
  | 'teams'
  | 'settings'
  | 'activity-logs'
  | 'todos'
  | 'checklists'
  | 'checklist-template'
  | 'library'
  | 'glossary'
  | 'order'
  | 'time'
  | 'orders-pos'
  | 'orders-invoices'
  | 'orders-shipments'
  | 'help-articles'
  | 'dashboards'
  | 'commissions'
  | 'courses'
  | 'proposals'
  | 'proposals-items'
  | 'proposal-templates'
  | 'timeMaterial'
  | 'tm-estimate-fees'
  | 'tm-estimate-estimator'
  | 'tm-adjust-categories'
  | 'tm-adjust-fees'
  | 'tm-changeorder-discount'
  | 'tm-co-view'
  | 'tm-co-create'
  | 'tm-co-approve'
  | 'ar-dashboard'
  | 'advanced-reporting'
  | 'projectDeadline'
  | 'change-default-payment'
  | 'dashboards-view'
  | 'impersonate'
  | 'dashboards-create'
  | 'finance'
  | 'manage-qbo-provider-invoice-sync'
  | 'deal-salesperson';

export interface GCPermission {
  key: string;
  title: string;
  description?: string;
  module: GCModule;
  resourceId?: string | number;
  children?: GCPermission[];
  detailed?: boolean;
  defaultValue?: 'all' | 'me' | 'none';
  value?: boolean;
  ids?: string[];
  items?: FormSelectItem<any>[];
  isSingle?: boolean;
}

function createPermissionItem<T>(items: T[], id: keyof T, title: keyof T, module: GCModule): GCPermission[] {
  return items.map(item => ({ title: R.toString(item[title]), module, key: `${module}-${item[id]}`, resourceId: R.toString(item[id]) }));
}

export enum GCModuleEnum {
  contacts = 'contacts',
  organizations = 'organizations',
  deals = 'deals',
  projects = 'projects',
  projectBoard = 'project-board',
  projectTemplate = 'template',
  support = 'support',
  orders = 'orders',
  ordersInvoices = 'orders-invoices',
  ordersShipments = 'orders-shipments',
  ordersPos = 'orders-pos',
  tmPos = 'tm-pos',
  tmLabor = 'tm-labor',
  tmPM = 'tm-pm',
  tmChangeOrders = 'tm-changeorders',
  tmSummary = 'tm-summary',
  tmPricing = 'tm-pricing',
  tmRMA = 'tm-rma',
  ticketing = 'ticketing',
  categories = 'categories',
  marketing = 'marketing',
  marketingBoard = 'marketing-board',
  marketingChecklists = 'marketing-checklists',
  reports = 'reports',
  reportBilling = 'reports-billing',
  reportContacts = 'reports-contacts',
  reportProjects = 'reports-projects',
  reportSales = 'reports-sales',
  reportSupport = 'reports-support',
  reportsTm = 'reports-time-material',
  companies = 'companies',
  users = 'users',
  activityLogs = 'activity-logs',
  teams = 'teams',
  settings = 'settings',
  helpArticles = 'help-articles',
  people = 'people',
  todo = 'todos',
  checklists = 'checklists',
  library = 'library',
  dashboards = 'dashboards',
  commissions = 'commissions',
  glossary = 'glossary',
  proposals = 'proposals',
  proposalTemplates = 'proposal-templates',
  proposalItems = 'proposal-items',
  timeMaterial = 'timeMaterial',
  tmEstimateFees = 'tm-estimate-fees',
  tmEstimateEstimator = 'tm-estimate-estimator',
  tmAdjustCategories = 'tm-adjust-categories',
  tmAdjustFees = 'tm-adjust-fees',
  tmChangeOrderDiscount = 'tm-changeorder-discount',
  tmChangeOrderView = 'tm-co-view',
  tmChangeOrderCreate = 'tm-co-create',
  tmChangeOrderApprove = 'tm-co-approve',
  projectDeadline = 'projectDeadline',
  advancedReporting = 'advanced-reporting',
  dashboardView = 'dashboards-view',
  dashboardCreate = 'dashboards-create',
  tools = 'tools',
  presentations = 'presentations',
  impersonate = 'impersonate',
  finance = 'finance',
}

export const generatePermissions = (
  dealBoards: CompactDealBoardResponse[],
  projectBoards: BoardResponse[],
  tmBoards: CompactTMBoardResponse[],
  channels: CompactChannelResponse[],
  templates: CompactChecklistTemplateResponse[],
  isOrders = false,
  isTM = false,
  dashboards: ARDashboardResponse[] = [],
  nDashboards: VeryCompactDashboardResponse[] = [],
  estimateFees: TMEstimatorTemplateFeeResponse[] = [],
  changeOrderTypes: TMChangeOrderTypeResponse[] = [],
) => {
  const items: GCPermission[] = R.compact([
    {
      title: 'Dashboards',
      module: 'dashboards',
      key: 'dashboards',
      detailed: true,
      children: [
        {
          title: 'All Dashboards',
          key: 'dashboards-view',
          module: 'dashboards-view',
          detailed: true,
          items: CommonService.selectItems(
            [
              {
                name: 'manage',
                value: 'View & Manage All',
              },
              {
                name: 'view',
                value: 'View All',
              },
              {
                name: 'selected',
                value: 'View and/or Manage Specific Items',
              },
            ],
            'value',
            'name',
            true,
          ),
          children: createPermissionItem(nDashboards, 'id', 'title', 'dashboards-view'),
        },
        { title: 'Create Dashboard', module: 'dashboards-create', key: 'dashboards-create' },
      ],
    },
    {
      title: gcLabel('Sales'),
      module: 'contacts',
      key: 'contacts',
      detailed: true,
      children: [
        {
          title: gcLabel('Organizations'),
          key: 'organizations',
          module: 'organizations',
          children: [
            {
              title: 'Communications',
              key: 'communications',
              module: 'organizations',
            },
            {
              title: 'Billing',
              key: 'billing',
              module: 'organizations',
            },
            {
              title: 'Files',
              key: 'files',
              module: 'organizations',
              detailed: true,
              isSingle: true,
              items: CommonService.selectItems(
                [
                  {
                    name: 'manage',
                    value: 'View & Manage All',
                  },
                  {
                    name: 'view',
                    value: 'View All',
                  },
                  {
                    name: 'selected',
                    value: 'View and/or Manage Specific Items',
                  },
                ],
                'value',
                'name',
                true,
              ),
            },
            {
              title: 'Transactions',
              key: 'transactions',
              module: 'organizations',
              detailed: true,
              isSingle: true,
              items: CommonService.selectItems(
                [
                  {
                    name: 'manage',
                    value: 'View & Manage All',
                  },
                  {
                    name: 'view',
                    value: 'View All',
                  },
                ],
                'value',
                'name',
                true,
              ),
            },
          ],
        },
        {
          title: gcLabel('Deals'),
          key: 'deals',
          module: 'deals',
          children: [
            ...createPermissionItem(dealBoards, 'id', 'name', 'deals'),
            // On/off: may assign, change or unassign a deal's Salesperson / Deal Owner (own module so it
            // never mixes with the board-access rows under 'deals'). Also enforced by the backend.
            { title: 'Assign/Change Deal Salesperson', module: 'deal-salesperson', key: 'assignDealSalesperson' },
          ],
        },
        { title: 'Contact', key: 'people', module: 'people' },
        {
          title: 'Proposals',
          module: 'proposals',
          key: 'proposals',
          children: [
            { title: 'Proposals', module: 'proposals-items', key: 'proposals-items', detailed: true },
            {
              title: 'Templates',
              module: 'proposal-templates',
              key: 'proposal-templates',
              isSingle: true,
              detailed: true,
              items: CommonService.selectItems(
                [
                  {
                    name: 'manage',
                    value: 'View & Manage All',
                  },
                  {
                    name: 'view',
                    value: 'View All',
                  },
                ],
                'value',
                'name',
                true,
              ),
            },
            { title: 'Allow To Modify Fees On Orders', module: 'proposals-items', key: 'modifyFeeInOrders' },
            {
              title: 'Allow users to change default payment collection terms',
              module: 'change-default-payment',
              key: 'changeDefaultPayment',
            },
          ],
        },
      ],
    },
    {
      title: gcLabel('Projects'),
      module: 'projects',
      key: 'projects',
      detailed: true,
      children: [
        { title: 'Board', module: 'project-board', key: 'project-board', children: createPermissionItem(projectBoards, 'id', 'name', 'projects') },
        { title: 'Templates', module: 'template', key: 'template' },
        { title: 'Deadlines', module: 'projectDeadline', key: 'projectDeadline' },
      ],
    },
    {
      title: gcLabel('Support'),
      module: 'support',
      key: 'support',
      children: [
        { title: 'Ticketing', module: 'ticketing', key: 'ticketing', children: createPermissionItem(channels, 'id', 'label', 'ticketing') },
        { title: 'Categories', module: 'categories', key: 'categories' },
      ],
    },
    {
      title: gcLabel('Subscriptions'),
      module: 'marketing',
      key: 'marketing',
      detailed: true,
      children: [
        { title: 'Board', module: 'marketing-board', key: 'marketing-board' },
        { title: 'Checklists', module: 'marketing-checklists', key: 'marketing-checklists' },
      ],
    },
    {
      title: gcLabel('Reports'),
      module: 'reports',
      key: 'reports',
      //detailed: true,
      children: R.compact([
        { title: 'Billing', module: 'reports-billing', key: 'reports-billing', detailed: true },
        { title: `Contacts`, module: 'reports-contacts', key: 'reports-contacts', detailed: true },
        { title: `${gcLabel('Projects')}`, module: 'reports-projects', key: 'reports-projects', detailed: true },
        { title: `Sales`, module: 'reports-sales', key: 'reports-sales', detailed: true },
        { title: `${gcLabel('Support')}`, module: 'reports-support', key: 'reports-support', detailed: true },
        { title: `${gcLabel('Time & Materials')}`, module: 'reports-time-material', key: 'reports-time-material', detailed: true },
        SettingsConfig.current()?.accountSettings.advancedReporting && {
          title: 'Advanced Reporting',
          module: 'advanced-reporting',
          key: 'advanced-reporting',
          description: 'Only admins can edit the reporting dashboards. Checking the box next to a specific dashboard will allow View permissions.',
          children: createPermissionItem(dashboards, 'id', 'title', 'ar-dashboard'),
        },
      ]),
    },
    { title: 'Manage Companies', module: 'companies', key: 'companies' },
    { title: 'Users', module: 'users', key: 'users' },
    { title: 'Impersonate', module: 'impersonate', key: 'impersonate' },
    { title: 'Activity Logs', module: 'activity-logs', key: 'activity-logs', detailed: true },
    { title: 'Teams', module: 'teams', key: 'teams' },
    { title: 'Settings', module: 'settings', key: 'settings' },
    // { title: 'Help Articles', module: 'help-articles', key: 'help-articles' },
    { title: 'To-Dos', module: 'todos', key: 'todos', detailed: true },
    {
      title: 'Checklists',
      module: 'checklists',
      key: 'checklists',
      detailed: true,
    },
    // {
    //   title: 'Checklist - Templates',
    //   module: 'checklist-template',
    //   key: 'checklist-template',
    //   children: createPermissionItem(templates, 'id', 'title', 'checklist-template'),
    // },
    {
      title: 'Checklist - Templates',
      module: 'checklist-template',
      key: 'checklist-template-new',
      detailed: true,
      isSingle: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    {
      title: 'Courses',
      module: 'courses',
      key: 'courses',
      detailed: true,
      isSingle: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    {
      title: 'Library',
      module: 'library',
      key: 'library',
      detailed: true,
      isSingle: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
          {
            name: 'selected',
            value: 'View and/or Manage Specific Items',
          },
        ],
        'value',
        'name',
        true,
      ),
    },
    {
      title: 'Glossary',
      module: 'glossary',
      key: 'glossary',
      isSingle: true,
      detailed: true,
      items: CommonService.selectItems(
        [
          {
            name: 'manage',
            value: 'View & Manage All',
          },
          {
            name: 'view',
            value: 'View All',
          },
        ],
        'value',
        'name',
        true,
      ),
    },

    {
      title: 'Commissions',
      module: 'commissions',
      key: 'commissions',
      detailed: true,
      defaultValue: 'me',
    },
    isOrders
      ? {
          title: 'Orders',
          module: 'orders',
          key: 'orders',
          children: [
            { title: 'Manage POs', module: 'orders-pos', key: 'orders-pos' },
            { title: 'Manage Invoices', module: 'orders-invoices', key: 'orders-invoices' },
            { title: 'Manage Shipments', module: 'orders-shipments', key: 'orders-shipments' },
          ],
        }
      : null,

    isTM
      ? {
          title: 'Time and Materials',
          module: 'timeMaterial',
          key: 'timeMaterial',
          detailed: true,
          children: [
            {
              title: 'Board',
              module: 'tm-board',
              key: 'tm-board',
              children: createPermissionItem(
                tmBoards.filter(o => R.toString(o.id) !== '9999'),
                'id',
                'name',
                'timeMaterial',
              ),
            },
            {
              title: 'Estimate/Time and Materials Fees',
              module: 'tm-estimate-fees',
              key: 'tm-estimate-fees',
              // detailed: true,
              children: createPermissionItem(estimateFees, 'id', 'name', 'tm-estimate-fees'),
            },
            // Change or remove an estimate's estimator on the estimate page (picking one on a new estimate is free).
            // Admins always can.
            { title: 'Change Estimate Estimator', module: 'tm-estimate-estimator', key: 'tm-estimate-estimator' },
            { title: 'Manage POs', module: 'tm-pos', key: 'tm-pos' },
            { title: 'Manage PM', module: 'tm-pm', key: 'tm-pm' },
            { title: 'Manage Labor', module: 'tm-labor', key: 'tm-labor' },
            { title: 'Manage Change Orders', module: 'tm-changeorders', key: 'tm-changeorders' },
            // Admins always can; the account also needs "Change Order Discounts" on (succeed-admin)
            { title: 'Add Discounts to Change Orders', module: 'tm-changeorder-discount', key: 'tm-changeorder-discount' },
            // One checkbox per change order type. View, Create or Approve lets the user see that type's change orders;
            // Approve gives the internal approval (or sends it to the client). Admins always can.
            {
              title: 'View Change Orders',
              module: 'tm-co-view',
              key: 'tm-co-view',
              children: createPermissionItem(changeOrderTypes, 'id', 'name', 'tm-co-view'),
            },
            {
              title: 'Create Change Orders',
              module: 'tm-co-create',
              key: 'tm-co-create',
              children: createPermissionItem(changeOrderTypes, 'id', 'name', 'tm-co-create'),
            },
            {
              title: 'Approve Change Orders',
              module: 'tm-co-approve',
              key: 'tm-co-approve',
              children: createPermissionItem(changeOrderTypes, 'id', 'name', 'tm-co-approve'),
            },
            { title: 'Manage RMA', module: 'tm-rma', key: 'tm-rma' },
            { title: 'Manage Summary', module: 'tm-summary', key: 'tm-summary' },
            // Summary tab adjustments (each change needs a note and is kept in the history). Admins always can.
            { title: 'Adjust Category Amounts (Summary)', module: 'tm-adjust-categories', key: 'tm-adjust-categories' },
            {
              title: 'Adjust Fee Amounts (Summary)',
              module: 'tm-adjust-fees',
              key: 'tm-adjust-fees',
              children: createPermissionItem(estimateFees, 'id', 'name', 'tm-adjust-fees'),
            },
            { title: 'Manage Pricing', module: 'tm-pricing', key: 'tm-pricing' },
          ],
        }
      : null,

    isTM
      ? {
          title: 'Finance',
          module: 'finance',
          key: 'finance',
          // detailed: true,
          children: [{ title: 'Manage QBO Vendor Invoice Sync', module: 'manage-qbo-provider-invoice-sync', key: 'manage-qbo-provider-invoice-sync' }],
        }
      : null,
  ]);
  return items;
};
